import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  IconButton,
  Alert,
  CircularProgress,
  Grid,
  FormControlLabel,
  Checkbox,
  List,
  ListItem,
  ListItemText,
  Divider
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Event as EventIcon,
  Search as SearchIcon,
  People as PeopleIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { EventImageUpload } from './EventImageUpload';

interface Event {
  id: string;
  name: string;
  eventDate: string;
  startTime: string;
  endTime?: string;
  location?: string;
  description?: string;
  maxCapacity?: number;
  isMandatory: boolean;
  dressCode?: string;
  imageUrl?: string;
  registrationCount?: number;
  created_at?: string;
  updated_at?: string;
}

interface RegisteredGuest {
  id: string;
  fullName: string;
  email: string;
}

export const EventManagement: React.FC = () => {
  const [events, setEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Create/Edit event dialog
  const [eventDialogOpen, setEventDialogOpen] = useState(false);
  const [editingEvent, setEditingEvent] = useState<Event | null>(null);
  const [eventForm, setEventForm] = useState({
    name: '',
    eventDate: '',
    startTime: '',
    endTime: '',
    location: '',
    description: '',
    maxCapacity: '',
    isMandatory: false,
    dressCode: ''
  });
  const [eventFormLoading, setEventFormLoading] = useState(false);
  
  // Image upload state
  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [imageUploadLoading, setImageUploadLoading] = useState(false);
  
  // Registered guests dialog state
  const [guestsDialogOpen, setGuestsDialogOpen] = useState(false);
  const [selectedEventForGuests, setSelectedEventForGuests] = useState<Event | null>(null);
  const [registeredGuests, setRegisteredGuests] = useState<RegisteredGuest[]>([]);
  const [loadingGuests, setLoadingGuests] = useState(false);
  const [guestsError, setGuestsError] = useState<string | null>(null);
  const [imageError, setImageError] = useState<string | null>(null);
  const [removeImageConfirmOpen, setRemoveImageConfirmOpen] = useState(false);
  
  // Delete confirmation dialog
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [eventToDelete, setEventToDelete] = useState<Event | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  useEffect(() => {
    loadEvents();
  }, []);

  const loadEvents = async () => {
    try {
      const isDemoMode = (window as any).DEMO_MODE;
      
      if (isDemoMode) {
        const { mockApiCall } = await import('../services/demoData');
        const data = await mockApiCall('/api/admin/events');
        setEvents(data.data.events || []);
        setLoading(false);
        return;
      }

      console.log('[EventManagement] Loading events...');

      const { ApiService } = await import('../services/api');
      const data = await ApiService.getEvents();
      
      console.log('[EventManagement] API response:', data);
      
      if (data.success) {
        const eventsList = data.data.events || data.data || [];
        console.log('[EventManagement] Events loaded:', eventsList.length, 'events');
        console.log('[EventManagement] First event imageUrl:', eventsList[0]?.imageUrl);
        setEvents(Array.isArray(eventsList) ? eventsList : []);
      } else {
        throw new Error(data.error || 'Failed to load events');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load events');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateEvent = () => {
    setEditingEvent(null);
    setEventForm({
      name: '',
      eventDate: '',
      startTime: '',
      endTime: '',
      location: '',
      description: '',
      maxCapacity: '',
      isMandatory: false,
      dressCode: ''
    });
    setSelectedImage(null);
    setImageError(null);
    setEventDialogOpen(true);
  };

  const handleEditEvent = (event: Event) => {
    setEditingEvent(event);
    setEventForm({
      name: event.name,
      eventDate: event.eventDate,
      startTime: event.startTime,
      endTime: event.endTime || '',
      location: event.location || '',
      description: event.description || '',
      maxCapacity: event.maxCapacity?.toString() || '',
      isMandatory: event.isMandatory,
      dressCode: event.dressCode || ''
    });
    setSelectedImage(null);
    setImageError(null);
    setEventDialogOpen(true);
  };

  const handleSaveEvent = async () => {
    if (!eventForm.name || !eventForm.eventDate || !eventForm.startTime) {
      setError('Name, date, and start time are required');
      return;
    }

    setEventFormLoading(true);
    setImageError(null);
    
    try {
      const { ApiService } = await import('../services/api');
      
      // Ensure date is in YYYY-MM-DD format without timezone conversion
      // The date input already gives us YYYY-MM-DD in local time
      const eventData = {
        name: eventForm.name,
        eventDate: eventForm.eventDate, // Already in YYYY-MM-DD format
        startTime: eventForm.startTime,
        endTime: eventForm.endTime || undefined,
        location: eventForm.location || undefined,
        description: eventForm.description || undefined,
        maxCapacity: eventForm.maxCapacity ? parseInt(eventForm.maxCapacity) : undefined,
        isMandatory: eventForm.isMandatory,
        dressCode: eventForm.dressCode || undefined
      };

      let result;
      let eventId: string;
      
      if (editingEvent) {
        result = await ApiService.updateEvent(editingEvent.id, eventData);
        eventId = editingEvent.id;
      } else {
        result = await ApiService.createEvent(eventData);
        eventId = result.data?.id;
      }

      if (!result.success) {
        throw new Error(result.error || 'Failed to save event');
      }

      // Handle image upload if a new image was selected
      if (selectedImage && eventId) {
        setImageUploadLoading(true);
        try {
          const uploadResult = await ApiService.uploadEventImage(eventId, selectedImage);
          if (!uploadResult.success) {
            throw new Error(uploadResult.error || 'Failed to upload image');
          }
          // Clear any previous errors on success
          setImageError(null);
        } catch (uploadErr) {
          // Log error but don't fail the entire operation
          console.error('Image upload failed:', uploadErr);
          const errorMessage = uploadErr instanceof Error ? uploadErr.message : 'Failed to upload image';
          setImageError(errorMessage);
          
          // Show user-friendly error message
          setError(`Event saved, but image upload failed: ${errorMessage}`);
          // Continue to close dialog and reload events
        } finally {
          setImageUploadLoading(false);
        }
      }

      setEventDialogOpen(false);
      setEditingEvent(null);
      setSelectedImage(null);
      loadEvents();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save event');
    } finally {
      setEventFormLoading(false);
    }
  };

  const handleImageSelect = (file: File) => {
    setSelectedImage(file);
    setImageError(null);
  };

  const handleImageRemove = () => {
    if (editingEvent?.imageUrl) {
      // Show confirmation dialog for existing images
      setRemoveImageConfirmOpen(true);
    } else {
      // Just clear the selected file for new uploads
      setSelectedImage(null);
      setImageError(null);
    }
  };

  const confirmImageRemoval = async () => {
    if (!editingEvent?.id) return;

    setImageUploadLoading(true);
    setImageError(null);
    
    try {
      const { ApiService } = await import('../services/api');
      const result = await ApiService.deleteEventImage(editingEvent.id);
      
      if (result.success) {
        // Update the editing event to remove imageUrl
        setEditingEvent({ ...editingEvent, imageUrl: undefined });
        setSelectedImage(null);
        setRemoveImageConfirmOpen(false);
        setImageError(null);
      } else {
        throw new Error(result.error || 'Failed to remove image');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to remove image';
      setImageError(errorMessage);
      
      // Show user-friendly error message
      setError(`Failed to remove image: ${errorMessage}`);
    } finally {
      setImageUploadLoading(false);
    }
  };

  const handleDeleteEvent = (event: Event) => {
    setEventToDelete(event);
    setDeleteDialogOpen(true);
  };

  const confirmDeleteEvent = async () => {
    if (!eventToDelete) return;

    setDeleteLoading(true);
    try {
      const { ApiService } = await import('../services/api');
      const result = await ApiService.deleteEvent(eventToDelete.id);
      
      if (result.success) {
        setDeleteDialogOpen(false);
        setEventToDelete(null);
        loadEvents();
      } else {
        throw new Error(result.error || 'Failed to delete event');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete event');
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleViewRegisteredGuests = async (event: Event) => {
    setSelectedEventForGuests(event);
    setGuestsDialogOpen(true);
    setLoadingGuests(true);
    setGuestsError(null);
    setRegisteredGuests([]);

    try {
      const apiUrl = process.env.NODE_ENV === 'production' 
        ? `https://3amqzkaklb.execute-api.us-east-1.amazonaws.com/prod/api/events/${event.id}/registered-guests`
        : `http://localhost:3001/api/events/${event.id}/registered-guests`;
      const authToken = localStorage.getItem('authToken');
      
      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          ...(authToken && { 'Authorization': `Bearer ${authToken}` })
        },
        credentials: 'include'
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error?.message || 'Failed to fetch registered guests');
      }

      const data = await response.json();
      // Map backend field names to frontend format
      const guests = (data.data || []).map((guest: any) => ({
        id: guest.id,
        fullName: guest.full_name || guest.fullName,
        email: guest.email
      }));
      setRegisteredGuests(guests);
    } catch (err) {
      setGuestsError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoadingGuests(false);
    }
  };

  const handleCloseGuestsDialog = () => {
    setGuestsDialogOpen(false);
    setSelectedEventForGuests(null);
    setRegisteredGuests([]);
    setGuestsError(null);
  };

  const filteredEvents = events.filter(event => {
    const matchesSearch = !searchTerm || 
      event.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (event.location && event.location.toLowerCase().includes(searchTerm.toLowerCase()));
    
    return matchesSearch;
  });

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
        <Typography variant="h5">
          Event Management
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleCreateEvent}
        >
          Create Event
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Filters */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={8}>
          <TextField
            fullWidth
            label="Search events"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
            }}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <Button
            variant="outlined"
            onClick={loadEvents}
            fullWidth
            sx={{ height: '56px' }}
          >
            Refresh
          </Button>
        </Grid>
      </Grid>

      {/* Events Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Image</TableCell>
              <TableCell>Event Name</TableCell>
              <TableCell>Date</TableCell>
              <TableCell>Time</TableCell>
              <TableCell>Location</TableCell>
              <TableCell>Capacity</TableCell>
              <TableCell>Registered/Capacity</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredEvents.map((event) => (
              <TableRow key={event.id}>
                <TableCell 
                  onClick={() => handleViewRegisteredGuests(event)}
                  sx={{ 
                    cursor: 'pointer', 
                    '&:hover': { bgcolor: 'action.hover' } 
                  }}
                >
                  {event.imageUrl ? (
                    <img
                      src={event.imageUrl}
                      alt={event.name}
                      crossOrigin="anonymous"
                      style={{
                        width: '60px',
                        height: '60px',
                        objectFit: 'cover',
                        borderRadius: '4px'
                      }}
                      onLoad={() => {
                        console.log('[EventManagement] Image loaded successfully:', event.imageUrl);
                      }}
                      onError={(e) => {
                        console.error('[EventManagement] Image failed to load:', event.imageUrl, e);
                        (e.target as HTMLImageElement).style.display = 'none';
                      }}
                    />
                  ) : (
                    <Box
                      sx={{
                        width: 60,
                        height: 60,
                        bgcolor: 'grey.200',
                        borderRadius: 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <EventIcon sx={{ color: 'grey.400' }} />
                    </Box>
                  )}
                </TableCell>
                <TableCell 
                  onClick={() => handleViewRegisteredGuests(event)}
                  sx={{ 
                    cursor: 'pointer', 
                    '&:hover': { bgcolor: 'action.hover' } 
                  }}
                >
                  <Box>
                    <Typography variant="body2" fontWeight="medium">
                      {event.name}
                    </Typography>
                    {event.description && (
                      <Typography variant="caption" color="text.secondary">
                        {event.description}
                      </Typography>
                    )}
                  </Box>
                </TableCell>
                <TableCell>
                  {event.eventDate}
                </TableCell>
                <TableCell>
                  {event.startTime}
                  {event.endTime && ` - ${event.endTime}`}
                </TableCell>
                <TableCell>{event.location || '-'}</TableCell>
                <TableCell>{event.maxCapacity || 'Unlimited'}</TableCell>
                <TableCell>
                  {event.isMandatory ? (
                    <Chip label="N/A - Mandatory" size="small" color="default" />
                  ) : (
                    <Box>
                      <Typography
                        variant="body2"
                        sx={{
                          color: event.maxCapacity && (event.registrationCount || 0) > event.maxCapacity
                            ? 'error.main'
                            : 'text.primary',
                          fontWeight: event.maxCapacity && (event.registrationCount || 0) > event.maxCapacity
                            ? 'bold'
                            : 'normal'
                        }}
                      >
                        {event.registrationCount || 0}
                        {event.maxCapacity ? `/${event.maxCapacity}` : '/Unlimited'}
                      </Typography>
                      {event.maxCapacity && (event.registrationCount || 0) > event.maxCapacity && (
                        <Chip
                          label="Over-registered"
                          size="small"
                          color="error"
                          sx={{ mt: 0.5 }}
                        />
                      )}
                    </Box>
                  )}
                </TableCell>
                <TableCell>
                  <Chip 
                    label={event.isMandatory ? 'Mandatory' : 'Optional'} 
                    size="small"
                    color={event.isMandatory ? 'primary' : 'default'}
                  />
                </TableCell>
                <TableCell>
                  <IconButton
                    size="small"
                    onClick={() => handleEditEvent(event)}
                    title="Edit event"
                  >
                    <EditIcon />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => handleDeleteEvent(event)}
                    title="Delete event"
                    color="error"
                  >
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            {filteredEvents.length === 0 && (
              <TableRow>
                <TableCell colSpan={9} align="center">
                  <Typography variant="body2" color="text.secondary" sx={{ py: 4 }}>
                    No events found
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Create/Edit Event Dialog */}
      <Dialog open={eventDialogOpen} onClose={() => setEventDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingEvent ? 'Edit Event' : 'Create New Event'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <EventImageUpload
                  currentImageUrl={editingEvent?.imageUrl}
                  onImageSelect={handleImageSelect}
                  onImageRemove={handleImageRemove}
                  loading={imageUploadLoading}
                  error={imageError || undefined}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Event Name"
                  value={eventForm.name}
                  onChange={(e) => setEventForm({ ...eventForm, name: e.target.value })}
                  required
                  sx={{ mb: 2 }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Location"
                  value={eventForm.location}
                  onChange={(e) => setEventForm({ ...eventForm, location: e.target.value })}
                  sx={{ mb: 2 }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Date"
                  type="date"
                  value={eventForm.eventDate}
                  onChange={(e) => setEventForm({ ...eventForm, eventDate: e.target.value })}
                  required
                  InputLabelProps={{ shrink: true }}
                  sx={{ mb: 2 }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="Start Time"
                  type="time"
                  value={eventForm.startTime}
                  onChange={(e) => setEventForm({ ...eventForm, startTime: e.target.value })}
                  required
                  InputLabelProps={{ shrink: true }}
                  sx={{ mb: 2 }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="End Time"
                  type="time"
                  value={eventForm.endTime}
                  onChange={(e) => setEventForm({ ...eventForm, endTime: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                  sx={{ mb: 2 }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Max Capacity"
                  type="number"
                  value={eventForm.maxCapacity}
                  onChange={(e) => setEventForm({ ...eventForm, maxCapacity: e.target.value })}
                  placeholder="Leave empty for unlimited"
                  sx={{ mb: 2 }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={eventForm.isMandatory}
                      onChange={(e) => setEventForm({ ...eventForm, isMandatory: e.target.checked })}
                    />
                  }
                  label="Mandatory Event"
                  sx={{ mt: 1 }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Description"
                  multiline
                  rows={3}
                  value={eventForm.description}
                  onChange={(e) => setEventForm({ ...eventForm, description: e.target.value })}
                  placeholder="Optional event description"
                />
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Dress Code"
                  value={eventForm.dressCode}
                  onChange={(e) => setEventForm({ ...eventForm, dressCode: e.target.value })}
                  placeholder="e.g., Formal, Casual, Black Tie, etc."
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEventDialogOpen(false)} disabled={eventFormLoading}>
            Cancel
          </Button>
          <Button 
            onClick={handleSaveEvent} 
            variant="contained"
            disabled={eventFormLoading}
            startIcon={eventFormLoading ? <CircularProgress size={20} /> : <EventIcon />}
          >
            {eventFormLoading ? 'Saving...' : (editingEvent ? 'Update Event' : 'Create Event')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Delete Event</DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Are you sure you want to delete this event? This action cannot be undone.
          </Typography>
          {eventToDelete && (
            <Box sx={{ p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
              <Typography variant="subtitle2">Event Details:</Typography>
              <Typography variant="body2">Name: {eventToDelete.name}</Typography>
              <Typography variant="body2">Date: {eventToDelete.eventDate}</Typography>
              <Typography variant="body2">Time: {eventToDelete.startTime}</Typography>
            </Box>
          )}
          <Alert severity="warning" sx={{ mt: 2 }}>
            <Typography variant="body2">
              This will also remove all guest registrations for this event.
            </Typography>
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} disabled={deleteLoading}>
            Cancel
          </Button>
          <Button 
            onClick={confirmDeleteEvent} 
            variant="contained" 
            color="error"
            disabled={deleteLoading}
            startIcon={deleteLoading ? <CircularProgress size={20} /> : <DeleteIcon />}
          >
            {deleteLoading ? 'Deleting...' : 'Delete Event'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Remove Image Confirmation Dialog */}
      <Dialog open={removeImageConfirmOpen} onClose={() => setRemoveImageConfirmOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Remove Event Image</DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Are you sure you want to remove this event image?
          </Typography>
          <Alert severity="info">
            <Typography variant="body2">
              You can upload a new image at any time.
            </Typography>
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRemoveImageConfirmOpen(false)} disabled={imageUploadLoading}>
            Cancel
          </Button>
          <Button 
            onClick={confirmImageRemoval} 
            variant="contained" 
            color="error"
            disabled={imageUploadLoading}
            startIcon={imageUploadLoading ? <CircularProgress size={20} /> : <DeleteIcon />}
          >
            {imageUploadLoading ? 'Removing...' : 'Remove Image'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Registered Guests Dialog */}
      <Dialog open={guestsDialogOpen} onClose={handleCloseGuestsDialog} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PeopleIcon />
            <Typography variant="h6">Registered Guests</Typography>
          </Box>
          <IconButton onClick={handleCloseGuestsDialog} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {selectedEventForGuests && (
            <Box>
              <Typography variant="subtitle1" gutterBottom>
                {selectedEventForGuests.name}
              </Typography>
              <Typography variant="body2" color="text.secondary" gutterBottom sx={{ mb: 2 }}>
                {selectedEventForGuests.eventDate} at {selectedEventForGuests.startTime}
              </Typography>

              {loadingGuests ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress />
                </Box>
              ) : guestsError ? (
                <Alert severity="error">{guestsError}</Alert>
              ) : registeredGuests.length === 0 ? (
                <Alert severity="info">No guests registered for this event yet.</Alert>
              ) : (
                <>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {registeredGuests.length} guest{registeredGuests.length !== 1 ? 's' : ''} registered
                    {selectedEventForGuests.maxCapacity && ` (${selectedEventForGuests.maxCapacity} capacity)`}
                  </Typography>
                  <List sx={{ maxHeight: 400, overflow: 'auto' }}>
                    {registeredGuests.map((guest, index) => (
                      <React.Fragment key={guest.id}>
                        <ListItem>
                          <ListItemText
                            primary={guest.fullName}
                            secondary={guest.email}
                          />
                        </ListItem>
                        {index < registeredGuests.length - 1 && <Divider />}
                      </React.Fragment>
                    ))}
                  </List>
                </>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseGuestsDialog}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};